<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, vue/no-static-inline-styles -->
<script setup>
// Layout 1:1 com /index real Lais (extraído do dynamic.js):
//   Title "Início"
//   Texto "Veja o panorama de todos os clientes - incluindo interessados, proprietários e inquilinos."
//   Cards: "Atendimentos no mês", "Cobranças de atendimento"
//   Gráficos: "Clientes nos últimos 6 meses", "Clientes por canal", "Clientes por dia da semana", "Clientes por horário"
//   CTA: "Atualize seu plano e economize com mais clientes atendidos"
import { computed, ref, onMounted, onBeforeUnmount, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import RelatorioAPI from 'dashboard/api/relatorio';
import FinanceiroAPI from 'dashboard/api/financeiro';

const periodos = [
  { value: 'mes-atual', label: 'Mês atual' },
  { value: 'mes-anterior', label: 'Mês anterior' },
  { value: 'ultimos-6m', label: 'Últimos 6 meses' },
  { value: 'ultimos-12m', label: 'Últimos 12 meses' },
];
const visoes = [
  { value: 'geral', label: 'Visão Geral', operacao: null },
  { value: 'aluguel', label: 'Aluguel', operacao: 'aluguel' },
  { value: 'compra', label: 'Compra', operacao: 'venda' },
];
const route = useRoute();
const router = useRouter();
const routeString = value => (Array.isArray(value) ? value[0] : value);
const normalizePeriodo = value =>
  periodos.some(period => period.value === value) ? value : 'mes-atual';
const normalizeVisao = value =>
  visoes.some(item => item.value === value) ? value : 'geral';

const periodo = ref(normalizePeriodo(routeString(route.query.periodo)));
const visao = ref(normalizeVisao(routeString(route.query.visao)));
const DASHBOARD_WIDGETS = [
  { id: 'kpis', label: 'Indicadores principais' },
  { id: 'clientes', label: 'Clientes por período e canal' },
  { id: 'atividade', label: 'Dia da semana e plano' },
  { id: 'financeiro', label: 'Horários e cobranças' },
];
const DEFAULT_WIDGETS = DASHBOARD_WIDGETS.map(widget => widget.id);
const QUICK_LINKS = [
  {
    label: 'Atendimentos',
    helper: 'Abrir conversas',
    icon: 'i-lucide-message-circle-more',
    to: { name: 'home' },
  },
  {
    label: 'Clientes',
    helper: 'Qualificar clientes',
    icon: 'i-lucide-users-round',
    to: { name: 'lais_leads_index' },
  },
  {
    label: 'Visitas',
    helper: 'Agenda e follow-up',
    icon: 'i-lucide-calendar-check',
    to: { name: 'visitas_index' },
  },
  {
    label: 'Relatório',
    helper: 'Resultados e tráfego',
    icon: 'i-lucide-bar-chart-3',
    to: { name: 'relatorio_index' },
  },
  {
    label: 'Financeiro',
    helper: 'Cobranças e período',
    icon: 'i-lucide-receipt',
    to: { name: 'financeiro_index' },
  },
  {
    label: 'Planos',
    helper: 'Uso e assinatura',
    icon: 'i-lucide-badge-check',
    to: { name: 'planos_index' },
  },
];

const resumo = ref({
  atendimentos: 0,
  leads: 0,
  clientes_unicos: 0,
  conversas: 0,
  leads_qualificados: 0,
  visitas: 0,
  solicitacoes_visita: 0,
  atendimentos_fora_horario: 0,
});
const showCustomize = ref(false);
const visibleWidgetIds = ref([...DEFAULT_WIDGETS]);
const isRefreshing = ref(false);
const lastUpdatedAt = ref(null);
const leadsPorCanal = ref([]);
const leadsPorDia = ref([]);
const leadsPorHorario = ref([]);
const leadsPorMes = ref([]);
const leadsPorSemana = ref([]);
const statusPorCanal = ref([]);
const cobrancas = ref([]);
const financeiro = ref({
  proxima_cobranca: { status: 'indisponivel', valor: null, vencimento: null },
  plano_atual: { nome: '-', preco: null, atendimentos_ciclo: 0 },
  uso_ciclo: { atendimentos: 0, limite: 0, percentual: 0 },
});

const CANAL_COLORS = {
  api: '#e9b76f',
  channel_facebook_page: '#1877F2',
  channel_line: '#06c755',
  channel_sms: '#FF8585',
  channel_telegram: '#0088cc',
  channel_twilio_sms: '#F22F46',
  channel_twilio_whatsapp: '#25D366',
  channel_twitter_profile: '#1DA1F2',
  channel_web_widget: '#49317a',
  channel_whatsapp: '#25D366',
  email: '#4a5568',
  facebook: '#1877F2',
  line: '#06c755',
  sms: '#FF8585',
  telegram: '#0088cc',
  twilio_sms: '#F22F46',
  twilio_whatsapp: '#25D366',
  twitter: '#1DA1F2',
  web: '#49317a',
  whatsapp: '#25D366',
};

const CANAL_LABELS = {
  channel_facebook_page: 'Facebook',
  channel_line: 'Line',
  channel_sms: 'SMS',
  channel_telegram: 'Telegram',
  channel_twilio_sms: 'SMS',
  channel_twilio_whatsapp: 'WhatsApp',
  channel_twitter_profile: 'Twitter',
  channel_web_widget: 'Site',
  channel_whatsapp: 'WhatsApp',
  facebook: 'Facebook',
  sms: 'SMS',
  telegram: 'Telegram',
  twilio_sms: 'SMS',
  twilio_whatsapp: 'WhatsApp',
  web: 'Site',
  whatsapp: 'WhatsApp',
};
const STATUS_COLORS = {
  pronto: '#28E4A8',
  qualificado: '#49317a',
  nao_qualificado: '#e9b76f',
};
const KPI_INFO_TEXT =
  'Desconsidera clientes gerados por disparos ativos, como Remarketing e Atualização de imóveis.';

const seriesLabel = item =>
  String(
    item?.label || item?.canal || item?.dia || item?.mes || item?.key || ''
  ).trim();

const seriesValue = item =>
  Number(item?.valor ?? item?.total ?? item?.count ?? item?.quantidade ?? 0);

const normalizeChannelKey = label =>
  String(label || '')
    .replace(/^Channel::/, 'channel_')
    .replace(/::/g, '_')
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const formatChannelLabel = item => {
  const label = seriesLabel(item);
  const key = normalizeChannelKey(label);
  return CANAL_LABELS[key] || label || 'Desconhecido';
};

const resumoCobrancas = computed(() => {
  const abertas = cobrancas.value.filter(item =>
    ['aberto', 'atrasado', 'pendente'].includes(item.status)
  );
  const pagas = cobrancas.value.filter(item => item.status === 'pago');

  return {
    totalPago: pagas.reduce((sum, item) => sum + Number(item.valor || 0), 0),
    totalAberto: abertas.reduce(
      (sum, item) => sum + Number(item.valor || 0),
      0
    ),
    quantidadePagas: pagas.length,
    quantidadeAbertas: abertas.length,
  };
});

const kpis = computed(() => [
  {
    label: 'Atendimentos no mês',
    valor: resumo.value.atendimentos,
    delta: resumo.value.atendimentos_delta,
    icon: 'i-lucide-message-circle-more',
    format: 'number',
    subline:
      resumo.value.clientes_unicos > 0
        ? `Nesse mês, chegaram ${resumo.value.clientes_unicos} ${
            resumo.value.clientes_unicos === 1
              ? 'cliente único'
              : 'clientes únicos'
          }`
        : 'Nenhum cliente único no período.',
    info: KPI_INFO_TEXT,
  },
  {
    label: 'Cobrança de atendimento',
    valor: resumoCobrancas.value.totalAberto,
    delta: null,
    icon: 'i-lucide-receipt',
    format: 'currency',
  },
  {
    label: 'Conversas',
    valor: resumo.value.conversas,
    delta: resumo.value.conversas_delta,
    icon: 'i-lucide-messages-square',
    format: 'number',
    info: KPI_INFO_TEXT,
  },
  {
    label: 'Clientes qualificados',
    valor: resumo.value.leads_qualificados,
    delta: resumo.value.leads_qualificados_delta,
    icon: 'i-lucide-badge-check',
    format: 'number',
    info: KPI_INFO_TEXT,
  },
  {
    label: 'Solicitações via Lais Visita',
    valor: resumo.value.solicitacoes_visita,
    delta: resumo.value.solicitacoes_visita_delta,
    icon: 'i-lucide-calendar-plus',
    format: 'number',
    info: KPI_INFO_TEXT,
  },
  {
    label: 'Visitas agendadas',
    valor: resumo.value.visitas,
    delta: resumo.value.visitas_delta,
    icon: 'i-lucide-calendar-check',
    format: 'number',
    info: KPI_INFO_TEXT,
  },
]);

const linhaPontos = computed(() => {
  if (!leadsPorMes.value.length) return '';
  const max = Math.max(1, ...leadsPorMes.value.map(d => seriesValue(d)));
  const step = 100 / Math.max(1, leadsPorMes.value.length - 1);
  return leadsPorMes.value
    .map((d, i) => `${i * step},${100 - (seriesValue(d) / max) * 90}`)
    .join(' ');
});

const linhaArea = computed(() => {
  if (!leadsPorMes.value.length) return '';
  const step = 100 / Math.max(1, leadsPorMes.value.length - 1);
  return `0,100 ${linhaPontos.value} ${(leadsPorMes.value.length - 1) * step},100 Z`;
});

const canaisExibidos = computed(() =>
  leadsPorCanal.value.map(c => {
    const channelKey = normalizeChannelKey(seriesLabel(c));
    return {
      canal: formatChannelLabel(c),
      valor: seriesValue(c),
      color: CANAL_COLORS[channelKey] || '#49317a',
    };
  })
);

const totalCanal = computed(() =>
  canaisExibidos.value.reduce((s, c) => s + c.valor, 0)
);
const statusChannelColumns = computed(() => {
  const columns = new Set();
  statusPorCanal.value.forEach(row => {
    (row.canais || []).forEach(item => columns.add(item.canal));
  });
  return [...columns];
});
const statusPorCanalTotal = computed(() =>
  statusPorCanal.value.reduce((sum, row) => sum + Number(row.total || 0), 0)
);
const statusChannelValue = (row, channel) =>
  Number((row.canais || []).find(item => item.canal === channel)?.valor || 0);
const maxDia = computed(() =>
  Math.max(1, ...leadsPorDia.value.map(d => seriesValue(d)))
);
const maxHorario = computed(() =>
  Math.max(1, ...leadsPorHorario.value.map(d => seriesValue(d)))
);
const maxSemana = computed(() =>
  Math.max(1, ...leadsPorSemana.value.map(d => seriesValue(d)))
);
const percentualPlano = computed(
  () => financeiro.value.uso_ciclo?.percentual || 0
);
const periodoSelecionadoLabel = computed(
  () => periodos.find(item => item.value === periodo.value)?.label || 'Período'
);
const visaoSelecionada = computed(
  () => visoes.find(item => item.value === visao.value) || visoes[0]
);
const hasHorarioData = computed(() =>
  leadsPorHorario.value.some(item => seriesValue(item) > 0)
);
const dashboardLayoutStorageKey = computed(
  () => `lais-dashboard-layout:${route.params.accountId || 'global'}`
);
const lastUpdatedLabel = computed(() =>
  lastUpdatedAt.value
    ? lastUpdatedAt.value.toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—'
);

const hydrateDashboardLayout = () => {
  if (typeof window === 'undefined') return;

  try {
    const raw = window.localStorage.getItem(dashboardLayoutStorageKey.value);
    const parsed = raw ? JSON.parse(raw) : null;
    const nextVisible = Array.isArray(parsed?.visible_widgets)
      ? parsed.visible_widgets.filter(id => DEFAULT_WIDGETS.includes(id))
      : DEFAULT_WIDGETS;
    visibleWidgetIds.value = nextVisible.length ? nextVisible : DEFAULT_WIDGETS;
  } catch {
    visibleWidgetIds.value = [...DEFAULT_WIDGETS];
  }
};

const persistDashboardLayout = () => {
  if (typeof window === 'undefined') return;

  window.localStorage.setItem(
    dashboardLayoutStorageKey.value,
    JSON.stringify({ visible_widgets: visibleWidgetIds.value })
  );
};

const isWidgetVisible = widgetId => visibleWidgetIds.value.includes(widgetId);

const toggleDashboardWidget = widgetId => {
  const nextVisible = isWidgetVisible(widgetId)
    ? visibleWidgetIds.value.filter(id => id !== widgetId)
    : [...visibleWidgetIds.value, widgetId];
  visibleWidgetIds.value = nextVisible.length ? nextVisible : [widgetId];
  persistDashboardLayout();
};

const resetDashboardLayout = () => {
  visibleWidgetIds.value = [...DEFAULT_WIDGETS];
  persistDashboardLayout();
};

const formatBRL = value =>
  value == null
    ? '—'
    : new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        maximumFractionDigits: 0,
      }).format(value);

const formatDateOnly = value => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('pt-BR');
};

const rangeForPeriodo = value => {
  const now = new Date();
  if (value === 'mes-anterior') {
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 0);
    return {
      inicio: start.toISOString().slice(0, 10),
      fim: end.toISOString().slice(0, 10),
      meses: 6,
    };
  }

  if (value === 'ultimos-12m') {
    return {
      inicio: new Date(now.getFullYear() - 1, now.getMonth(), 1)
        .toISOString()
        .slice(0, 10),
      fim: now.toISOString().slice(0, 10),
      meses: 12,
    };
  }

  if (value === 'ultimos-6m') {
    return {
      inicio: new Date(now.getFullYear(), now.getMonth() - 5, 1)
        .toISOString()
        .slice(0, 10),
      fim: now.toISOString().slice(0, 10),
      meses: 6,
    };
  }

  return {
    inicio: new Date(now.getFullYear(), now.getMonth(), 1)
      .toISOString()
      .slice(0, 10),
    fim: now.toISOString().slice(0, 10),
    meses: 6,
  };
};

const periodoMesLabel = computed(() => {
  const start = rangeForPeriodo(periodo.value).inicio;
  const date = new Date(`${start}T12:00:00`);
  return date.toLocaleDateString('pt-BR', { month: 'long' });
});

const load = async () => {
  isRefreshing.value = true;
  try {
    const nextQuery = { ...route.query };
    if (periodo.value === 'mes-atual') {
      delete nextQuery.periodo;
    } else {
      nextQuery.periodo = periodo.value;
    }
    if (visao.value === 'geral') {
      delete nextQuery.visao;
    } else {
      nextQuery.visao = visao.value;
    }
    router.replace({ query: nextQuery }).catch(() => {});

    const params = {
      ...rangeForPeriodo(periodo.value),
      ...(visaoSelecionada.value.operacao
        ? { operacao: visaoSelecionada.value.operacao }
        : {}),
    };
    const [
      resumoRes,
      canalRes,
      diaRes,
      horarioRes,
      semanaRes,
      mesRes,
      statusCanalRes,
      finRes,
      cobrancasRes,
    ] = await Promise.all([
      RelatorioAPI.resumo(params),
      RelatorioAPI.leadsPorCanal(params),
      RelatorioAPI.leadsPorDia(params),
      RelatorioAPI.leadsPorHorario(params),
      RelatorioAPI.leadsPorSemana(params),
      RelatorioAPI.leadsPorMes({
        meses: params.meses,
        ...(params.operacao ? { operacao: params.operacao } : {}),
      }),
      RelatorioAPI.statusPorCanal(params),
      FinanceiroAPI.resumo(),
      FinanceiroAPI.cobrancas(),
    ]);
    resumo.value = resumoRes.data;
    leadsPorCanal.value = canalRes.data.data || [];
    leadsPorDia.value = diaRes.data.data || [];
    leadsPorHorario.value = horarioRes.data.data || [];
    leadsPorSemana.value = semanaRes.data.data || [];
    leadsPorMes.value = mesRes.data.data || [];
    statusPorCanal.value = statusCanalRes.data.data || [];
    financeiro.value = finRes.data;
    cobrancas.value = cobrancasRes.data.data || [];
    lastUpdatedAt.value = new Date();
  } catch (e) {
    // Mantem os indicadores zerados se os dados ainda nao estiverem disponiveis.
  } finally {
    isRefreshing.value = false;
  }
};

let realtimeRefreshTimer = null;
const startRealtimeRefresh = () => {
  realtimeRefreshTimer = window.setInterval(() => {
    load();
  }, 60000);
};

onMounted(() => {
  hydrateDashboardLayout();
  load();
  startRealtimeRefresh();
});
onBeforeUnmount(() => {
  if (realtimeRefreshTimer) window.clearInterval(realtimeRefreshTimer);
});
watch(periodo, load);
watch(visao, load);
watch(
  () => route.query.periodo,
  value => {
    const nextPeriodo = normalizePeriodo(routeString(value));
    if (nextPeriodo !== periodo.value) {
      periodo.value = nextPeriodo;
    }
  }
);
watch(
  () => route.query.visao,
  value => {
    const nextVisao = normalizeVisao(routeString(value));
    if (nextVisao !== visao.value) {
      visao.value = nextVisao;
    }
  }
);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, vue/no-static-inline-styles -->
  <section
    class="flex flex-col h-full bg-white font-redHatText overflow-hidden"
  >
    <LaisHeader
      title="Início"
      helper="Veja o panorama de todos os clientes — incluindo interessados, proprietários e inquilinos."
    >
      <template #actions>
        <div class="flex flex-wrap items-center justify-end gap-2">
          <span class="text-[12px] font-medium text-lais-text-muted">
            Atualizado às {{ lastUpdatedLabel }}
          </span>
          <button
            type="button"
            class="grid size-9 place-items-center rounded-lais border border-[#e5e7eb] text-lais-text-body transition-colors hover:border-lais-primary hover:text-lais-primary disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="isRefreshing"
            title="Atualizar métricas"
            aria-label="Atualizar métricas"
            @click="load"
          >
            <span
              class="size-4"
              :class="
                isRefreshing
                  ? 'i-lucide-loader-circle animate-spin'
                  : 'i-lucide-refresh-cw'
              "
              aria-hidden="true"
            />
          </button>
          <button
            type="button"
            class="grid size-9 place-items-center rounded-lais border border-[#e5e7eb] text-lais-text-body transition-colors hover:border-lais-primary hover:text-lais-primary"
            :class="showCustomize ? 'border-lais-primary bg-[#f8f4fc]' : ''"
            title="Customizar dashboard"
            aria-label="Customizar dashboard"
            @click="showCustomize = !showCustomize"
          >
            <span
              class="i-lucide-sliders-horizontal size-4"
              aria-hidden="true"
            />
          </button>
          <div
            class="inline-flex h-9 overflow-hidden rounded-lais border border-[#e5e7eb] bg-white"
            aria-label="Visão do dashboard"
          >
            <button
              v-for="item in visoes"
              :key="item.value"
              type="button"
              class="px-3 text-[12px] font-semibold transition-colors"
              :class="
                visao === item.value
                  ? 'bg-lais-primary text-white'
                  : 'text-lais-text-body hover:bg-[#f8f4fc] hover:text-lais-primary'
              "
              @click="visao = item.value"
            >
              {{ item.label }}
            </button>
          </div>
          <select
            v-model="periodo"
            class="h-9 px-3 text-[13px] bg-white border border-[#e5e7eb] rounded-lais text-lais-text font-medium focus:outline-none focus:border-lais-primary"
          >
            <option v-for="p in periodos" :key="p.value" :value="p.value">
              {{ p.label }}
            </option>
          </select>
        </div>
      </template>
    </LaisHeader>

    <div class="flex-1 overflow-auto px-4 pt-4 w-full sm:px-6 lg:px-10 lg:pt-6">
      <section
        class="mb-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-6"
        aria-label="Atalhos rápidos"
      >
        <RouterLink
          v-for="link in QUICK_LINKS"
          :key="link.label"
          :to="link.to"
          class="group flex min-h-[86px] items-center gap-3 rounded-lais border border-[#e5e7eb] bg-white p-3 shadow-lais-card transition-colors hover:border-lais-primary hover:bg-[#fbf8ff]"
        >
          <span
            class="grid size-10 shrink-0 place-items-center rounded-lais-sm bg-[#f0e8fa] text-lais-primary transition-colors group-hover:bg-lais-primary group-hover:text-white"
          >
            <span :class="[link.icon]" class="size-4" aria-hidden="true" />
          </span>
          <span class="min-w-0">
            <span class="block text-[13px] font-semibold text-lais-text">
              {{ link.label }}
            </span>
            <span class="block truncate text-[12px] text-lais-text-body">
              {{ link.helper }}
            </span>
          </span>
        </RouterLink>
      </section>

      <section
        class="mb-6 flex flex-col gap-3 rounded-lais border border-[#e5e7eb] bg-[#fafafa] p-4 sm:flex-row sm:items-center sm:justify-between"
      >
        <div>
          <p class="text-[13px] font-semibold text-lais-text">
            Não perca o relatório de {{ periodoSelecionadoLabel.toLowerCase() }}
          </p>
          <p class="mt-1 text-[12px] text-lais-text-body">
            Veja o panorama de todos os clientes, incluindo clientes comerciais,
            proprietários e inquilinos.
          </p>
        </div>
        <RouterLink
          :to="{ name: 'relatorio_index' }"
          class="inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-lais bg-lais-primary px-4 text-[12px] font-semibold text-white transition-colors hover:bg-[#5f3f99]"
        >
          Ver relatório
          <span class="i-lucide-arrow-right size-4" aria-hidden="true" />
        </RouterLink>
      </section>

      <section
        v-if="showCustomize"
        class="mb-6 rounded-lais border border-[#e5e7eb] bg-[#fafafa] p-4"
      >
        <div class="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 class="text-[14px] font-semibold text-lais-text">
              Personalizar visão inicial
            </h2>
            <p class="mt-1 text-[12px] text-lais-text-body">
              Escolha os blocos exibidos neste dashboard.
            </p>
          </div>
          <button
            type="button"
            class="h-9 rounded-lais border border-[#e5e7eb] px-3 text-[12px] font-semibold text-lais-text-body hover:border-lais-primary hover:text-lais-primary"
            @click="resetDashboardLayout"
          >
            Restaurar padrão
          </button>
        </div>
        <div class="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <label
            v-for="widget in DASHBOARD_WIDGETS"
            :key="widget.id"
            class="flex items-center gap-3 rounded-lais-sm border border-[#e5e7eb] bg-white px-3 py-2 text-[13px] font-medium text-lais-text"
          >
            <input
              type="checkbox"
              class="size-4 accent-lais-primary"
              :checked="isWidgetVisible(widget.id)"
              @change="toggleDashboardWidget(widget.id)"
            />
            {{ widget.label }}
          </label>
        </div>
      </section>

      <!-- KPIs -->
      <section
        v-if="isWidgetVisible('kpis')"
        class="grid gap-4 grid-cols-2 md:grid-cols-3 2xl:grid-cols-6 mb-8"
      >
        <article
          v-for="(k, i) in kpis"
          :key="i"
          class="flex flex-col gap-3 p-4 bg-white border border-[#e5e7eb] rounded-lais shadow-lais-card"
        >
          <div class="flex items-center gap-2">
            <span
              class="size-4 text-lais-primary shrink-0"
              :class="[k.icon]"
              aria-hidden="true"
            />
            <span
              class="text-[11px] uppercase tracking-wide text-lais-text-muted truncate font-semibold flex-1"
            >
              {{ k.label }}
            </span>
            <button
              v-if="k.info"
              type="button"
              class="grid size-5 shrink-0 place-items-center rounded-full text-lais-text-muted transition-colors hover:bg-[#f8f4fc] hover:text-lais-primary"
              :title="k.info"
              :aria-label="k.info"
            >
              <span class="i-lucide-info size-3.5" aria-hidden="true" />
            </button>
            <span
              v-if="k.delta !== undefined && k.delta !== null"
              class="text-[11px] font-semibold tabular-nums shrink-0"
              :class="k.delta >= 0 ? 'text-lais-mint' : 'text-lais-danger'"
            >
              {{ k.delta >= 0 ? '+' : '' }}{{ k.delta }}%
            </span>
          </div>
          <p
            class="text-[28px] leading-none font-semibold text-lais-text font-redHatDisplay"
          >
            {{ k.format === 'currency' ? formatBRL(k.valor) : k.valor }}
          </p>
          <p
            v-if="k.subline"
            class="text-[12px] leading-snug text-lais-text-body"
          >
            {{ k.subline }}
          </p>
        </article>
      </section>

      <div
        v-if="isWidgetVisible('clientes')"
        class="grid gap-6 lg:grid-cols-2 mb-8"
      >
        <!-- Clientes nos últimos 6 meses (line chart) -->
        <LaisCard title="Clientes nos últimos 6 meses">
          <div v-if="leadsPorMes.length" class="h-48 w-full">
            <svg
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              class="w-full h-full"
            >
              <defs>
                <linearGradient id="laisLineFill" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stop-color="#49317a" stop-opacity="0.18" />
                  <stop offset="100%" stop-color="#49317a" stop-opacity="0" />
                </linearGradient>
              </defs>
              <polygon :points="linhaArea" fill="url(#laisLineFill)" />
              <polyline
                :points="linhaPontos"
                fill="none"
                stroke="#49317a"
                stroke-width="1.8"
                vector-effect="non-scaling-stroke"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <div class="flex justify-between mt-1 px-1">
              <span
                v-for="(d, i) in leadsPorMes"
                :key="i"
                class="text-[11px] text-lais-text-muted"
              >
                {{ seriesLabel(d) }}
              </span>
            </div>
          </div>
          <div
            v-else
            class="h-48 grid place-items-center text-lais-text-muted text-[12px]"
          >
            Sem dados no período.
          </div>
          <div
            v-if="resumo.atendimentos_fora_horario > 0"
            class="mt-4 flex items-start gap-3 rounded-lais border border-[#d8f7eb] bg-[#f0fdf8] p-3"
          >
            <span
              class="i-lucide-moon-star mt-0.5 size-4 shrink-0 text-[#1FB386]"
              aria-hidden="true"
            />
            <p class="text-[12px] leading-relaxed text-[#256c58]">
              {{ resumo.atendimentos_fora_horario }}
              {{
                resumo.atendimentos_fora_horario === 1
                  ? 'cliente foi atendido'
                  : 'clientes foram atendidos'
              }}
              fora do horário comercial.
            </p>
          </div>
        </LaisCard>

        <!-- Clientes por canal -->
        <LaisCard title="Clientes por canal">
          <ul v-if="canaisExibidos.length" class="flex flex-col gap-3">
            <li
              v-for="(c, i) in canaisExibidos"
              :key="i"
              class="flex flex-col gap-1.5"
            >
              <div class="flex items-center justify-between text-[13px]">
                <span class="text-lais-text flex items-center gap-2">
                  <span
                    class="size-2.5 rounded-full"
                    :style="{ backgroundColor: c.color }"
                  />
                  {{ c.canal }}
                </span>
                <span class="font-semibold tabular-nums text-lais-text">
                  {{ c.valor }}
                  <span
                    class="text-[11px] text-lais-text-muted font-normal ml-1"
                  >
                    ({{
                      totalCanal ? Math.round((c.valor / totalCanal) * 100) : 0
                    }}%)
                  </span>
                </span>
              </div>
              <div class="h-1.5 bg-[#f5f5f5] rounded-full overflow-hidden">
                <div
                  class="h-full rounded-full"
                  :style="{
                    width: `${totalCanal ? (c.valor / totalCanal) * 100 : 0}%`,
                    backgroundColor: c.color,
                  }"
                />
              </div>
            </li>
          </ul>
          <div
            v-else
            class="h-48 grid place-items-center text-lais-text-muted text-[12px]"
          >
            Sem clientes registrados.
          </div>
        </LaisCard>

        <LaisCard :title="`Clientes semanais em ${periodoMesLabel}`">
          <div
            v-if="leadsPorSemana.length"
            class="flex items-end justify-between h-48 gap-3 px-2"
          >
            <div
              v-for="(d, i) in leadsPorSemana"
              :key="i"
              class="flex flex-col items-center gap-2 flex-1"
            >
              <span
                class="text-[11px] font-semibold text-lais-text tabular-nums"
              >
                {{ seriesValue(d) }}
              </span>
              <div
                class="w-full bg-[#6f5aa2] rounded-t"
                :style="{
                  height: `${Math.max((seriesValue(d) / maxSemana) * 100, 4)}%`,
                }"
              />
              <span class="text-[11px] text-lais-text-muted">
                {{ seriesLabel(d) }}
              </span>
            </div>
          </div>
          <div
            v-else
            class="h-48 grid place-items-center text-lais-text-muted text-[12px]"
          >
            Sem dados no período.
          </div>
        </LaisCard>

        <LaisCard title="Status por canal">
          <div
            v-if="statusPorCanal.length && statusChannelColumns.length"
            class="overflow-x-auto no-scrollbar"
          >
            <table class="min-w-full text-left text-[12px]">
              <thead>
                <tr class="border-b border-[#e5e7eb] text-lais-text-muted">
                  <th class="py-2 pr-3 font-semibold">Status</th>
                  <th
                    v-for="channel in statusChannelColumns"
                    :key="channel"
                    class="px-3 py-2 text-right font-semibold"
                  >
                    {{ formatChannelLabel({ label: channel }) }}
                  </th>
                  <th class="py-2 pl-3 text-right font-semibold">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in statusPorCanal"
                  :key="row.status_key"
                  class="border-b border-[#f1f1f1] last:border-0"
                >
                  <td class="py-3 pr-3">
                    <span
                      class="inline-flex items-center gap-2 font-semibold text-lais-text"
                    >
                      <span
                        class="size-2.5 rounded-full"
                        :style="{
                          backgroundColor:
                            STATUS_COLORS[row.status_key] || '#a0aec0',
                        }"
                      />
                      {{ row.status_label }}
                    </span>
                  </td>
                  <td
                    v-for="channel in statusChannelColumns"
                    :key="`${row.status_key}-${channel}`"
                    class="px-3 py-3 text-right tabular-nums text-lais-text-body"
                  >
                    {{ statusChannelValue(row, channel) }}
                  </td>
                  <td
                    class="py-3 pl-3 text-right font-semibold tabular-nums text-lais-text"
                  >
                    {{ row.total }}
                  </td>
                </tr>
              </tbody>
            </table>
            <p class="mt-3 text-[11px] text-lais-text-muted">
              {{ statusPorCanalTotal }} clientes no período selecionado.
            </p>
          </div>
          <div
            v-else
            class="h-48 grid place-items-center text-lais-text-muted text-[12px]"
          >
            Sem clientes registrados.
          </div>
        </LaisCard>
      </div>

      <div
        v-if="isWidgetVisible('atividade')"
        class="grid gap-6 lg:grid-cols-2 mb-8"
      >
        <!-- Clientes por dia da semana -->
        <LaisCard title="Clientes por dia da semana">
          <div
            v-if="leadsPorDia.length"
            class="flex items-end justify-between h-48 gap-2 px-2"
          >
            <div
              v-for="(d, i) in leadsPorDia"
              :key="i"
              class="flex flex-col items-center gap-2 flex-1"
            >
              <span
                class="text-[11px] font-semibold text-lais-text tabular-nums"
              >
                {{ seriesValue(d) }}
              </span>
              <div
                class="w-full bg-lais-primary rounded-t"
                :style="{
                  height: `${(seriesValue(d) / maxDia) * 100}%`,
                  minHeight: '4px',
                }"
              />
              <span class="text-[11px] text-lais-text-muted">{{
                seriesLabel(d)
              }}</span>
            </div>
          </div>
          <div
            v-else
            class="h-48 grid place-items-center text-lais-text-muted text-[12px]"
          >
            Sem dados no período.
          </div>
        </LaisCard>

        <!-- CTA upgrade plano -->
        <article
          class="flex flex-col gap-3 p-6 bg-gradient-to-br from-lais-primary to-[#2E1F4D] text-white rounded-lais shadow-lais-pop"
        >
          <span class="i-lucide-rocket size-8 opacity-90" aria-hidden="true" />
          <p
            class="text-[12px] font-semibold uppercase tracking-wide text-white/70"
          >
            Atenda mais
          </p>
          <h3
            class="text-[18px] font-semibold font-redHatDisplay leading-tight"
          >
            Atualize seu plano e economize com mais clientes atendidos
          </h3>
          <p class="text-[13px] text-white/80 leading-relaxed">
            <template v-if="percentualPlano >= 100">
              Você atingiu 100% do consumo do plano.
            </template>
            <template v-else>
              Você atingiu
              <strong class="text-lais-mint">{{ percentualPlano }}%</strong>
              do seu plano atual.
            </template>
            Com o plano
            <strong class="text-white">{{
              financeiro.plano_atual.nome || 'Pro'
            }}</strong>
            você atende até
            <strong class="text-white">
              {{
                financeiro.plano_atual.leads_max ||
                financeiro.plano_atual.atendimentos_ciclo ||
                500
              }}
            </strong>
            clientes por mês com SLA prioritário.
          </p>
          <div class="mt-2">
            <RouterLink
              :to="{ name: 'planos_index' }"
              class="inline-flex items-center gap-2 h-10 px-5 text-[13px] font-semibold text-lais-primary bg-lais-mint rounded-lais hover:bg-[#1FB386] hover:text-white transition-colors"
            >
              Ver planos disponíveis
              <span class="i-lucide-arrow-right size-4" aria-hidden="true" />
            </RouterLink>
          </div>
        </article>
      </div>

      <div
        v-if="isWidgetVisible('financeiro')"
        class="grid gap-6 lg:grid-cols-2 mb-8"
      >
        <LaisCard title="Clientes por horário">
          <template #titleAction>
            <span
              class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
            >
              {{ periodoSelecionadoLabel }}
            </span>
          </template>
          <div v-if="hasHorarioData" class="overflow-x-auto no-scrollbar pb-1">
            <div class="flex items-end gap-2 min-w-[960px] h-56 px-2">
              <div
                v-for="(d, i) in leadsPorHorario"
                :key="i"
                class="flex flex-col items-center gap-2 w-8 shrink-0"
              >
                <span
                  class="text-[10px] font-semibold text-lais-text tabular-nums"
                >
                  {{ seriesValue(d) }}
                </span>
                <div
                  class="w-full bg-lais-primary rounded-t"
                  :style="{
                    height: seriesValue(d)
                      ? `${Math.max((seriesValue(d) / maxHorario) * 100, 6)}%`
                      : '2px',
                    opacity: seriesValue(d) ? 1 : 0.2,
                  }"
                />
                <span class="text-[10px] text-lais-text-muted">
                  {{ seriesLabel(d) }}
                </span>
              </div>
            </div>
          </div>
          <div
            v-else
            class="h-48 grid place-items-center text-lais-text-muted text-[12px]"
          >
            Sem dados no período.
          </div>
        </LaisCard>

        <LaisCard title="Cobranças de atendimento">
          <div class="grid gap-4 sm:grid-cols-2">
            <div class="rounded-lais border border-[#e5e7eb] bg-[#fafafa] p-4">
              <p
                class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
              >
                Pago
              </p>
              <p
                class="mt-2 text-[26px] font-semibold text-lais-text font-redHatDisplay"
              >
                {{ formatBRL(resumoCobrancas.totalPago) }}
              </p>
              <p class="mt-1 text-[12px] text-lais-text-body">
                {{ resumoCobrancas.quantidadePagas }} cobrança(s) quitadas
              </p>
            </div>
            <div class="rounded-lais border border-[#e5e7eb] bg-[#fafafa] p-4">
              <p
                class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
              >
                Em aberto
              </p>
              <p
                class="mt-2 text-[26px] font-semibold text-[#b45309] font-redHatDisplay"
              >
                {{ formatBRL(resumoCobrancas.totalAberto) }}
              </p>
              <p class="mt-1 text-[12px] text-lais-text-body">
                {{ resumoCobrancas.quantidadeAbertas }} cobrança(s) pendentes
              </p>
            </div>
          </div>

          <div class="mt-4 rounded-lais border border-[#e5e7eb] p-4">
            <p
              class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
            >
              Próxima cobrança
            </p>
            <p class="mt-2 text-[18px] font-semibold text-lais-primary">
              {{ formatBRL(financeiro.proxima_cobranca?.valor) }}
            </p>
            <p class="mt-1 text-[12px] text-lais-text-body">
              {{
                financeiro.proxima_cobranca?.vencimento
                  ? `Vencimento em ${formatDateOnly(financeiro.proxima_cobranca.vencimento)}`
                  : 'A cobrança será disponibilizada no início do mês.'
              }}
            </p>
            <p class="mt-1 text-[12px] text-lais-text-muted">
              Plano {{ financeiro.plano_atual?.nome || 'atual' }}
            </p>
          </div>
        </LaisCard>
      </div>
    </div>
  </section>
</template>
